/**
 * Gastos, pagos a proveedores y dinero del dueño.
 *
 * These are the movements that make a profit figure mean something. Everything up to here
 * recorded what the business bought and sold; this records what it spent to stay open, and —
 * the part that decides whether the books are honest — separates that from what the owner took
 * out for themselves.
 */

import {
  ZERO,
  addMoney,
  centavos,
  fechaEnNicaragua,
  formatMoney,
  fxRate,
  orderKey,
  subMoney,
  usdToCordobas,
  esCuentaDeBanco,
  esCuentaEnDolares,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
} from '@abastece/shared';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import { ACCOUNTS, type AccountKey } from '../ledger/accounts.js';
import { alOficial, cuentaCerradaParaSiempre, cuentaDeTransferencia, lineaDeBanco } from './bancos.js';
import { devolucionDePago, pagoDeCaja } from './caja.js';
import { contadaDespues, lugarEnElLog, yaLaContaron } from './contadas.js';
import { pendienteDeCompra, pendienteDeGasto } from '../state.js';
import type {
  ApplyResult,
  CategoriaGasto,
  Effect,
  FormaPago,
  Gasto,
  MovimientoDueno,
  PagoProveedor,
  Reader,
} from '../state.js';

/** Where each expense category lands in the ledger. One mapping, used everywhere. */
export const CUENTA_CATEGORIA: Record<CategoriaGasto, AccountKey> = {
  alquiler: 'ALQUILER',
  energia: 'ENERGIA',
  agua: 'AGUA',
  telefono_internet: 'TELEFONO_INTERNET',
  transporte: 'TRANSPORTE',
  mantenimiento: 'MANTENIMIENTO',
  impuestos_municipales: 'IMPUESTOS_MUNICIPALES',
  empaques: 'EMPAQUES',
  sueldos: 'SUELDOS',
  otros: 'OTROS_GASTOS',
};

export const ETIQUETA_CATEGORIA: Record<CategoriaGasto, string> = {
  alquiler: 'Alquiler',
  energia: 'Energía eléctrica',
  agua: 'Agua',
  telefono_internet: 'Teléfono e internet',
  transporte: 'Transporte y combustible',
  mantenimiento: 'Mantenimiento y reparaciones',
  impuestos_municipales: 'Impuestos municipales',
  empaques: 'Empaques y bolsas',
  sueldos: 'Sueldos y salarios',
  otros: 'Otros gastos',
};

export const CATEGORIAS_GASTO = Object.keys(CUENTA_CATEGORIA) as CategoriaGasto[];

/**
 * Where a payment comes from, unless it came out of an open till.
 *
 * The drawer is what a shift counts at the corte, so cash paid out of it says which till
 * (`sesionId`) and comes off «Caja (gaveta)» instead, and that corte stops expecting it
 * (`pagoDeCaja`). Everything else in cash is general cash.
 */
const CUENTA_PAGO: Record<FormaPago, AccountKey> = {
  efectivo_cordobas: 'CAJA_FUERTE',
  // Dollars too: the corte takes every dollar out of the drawer, so general cash is where they are.
  efectivo_dolares: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
  credito: 'CUENTAS_POR_PAGAR',
};

/** Where a payment comes from: by transfer, the bank account it names (`bancos.ts`). */
const cuentaDelPago = (forma: FormaPago, cuenta: string | null | undefined): AccountKey =>
  forma === 'transferencia' ? cuentaDeTransferencia(cuenta as CuentaBanco | null | undefined) : CUENTA_PAGO[forma];

/** A line on whichever account a payment came from: a dollar account's carrying the dollars it moved. */
function lineaDelPago(lado: 'debe' | 'haber', cuenta: AccountKey, cordobas: Centavos, dolares: number, memo?: string): JournalLine {
  if (esCuentaDeBanco(cuenta)) return lineaDeBanco(lado, cuenta, cordobas, dolares, memo);
  return lado === 'debe' ? debit(cuenta, cordobas, memo) : credit(cuenta, cordobas, memo);
}

/**
 * The events that count money and book what they find missing: a till's opening, corte, handover
 * and receipt, a route's settlement, and the safe's count. A shortage is explained against one of
 * these alone. Others book on a shortage account too, and found nothing missing: the void of an
 * explanation puts its shortage back, and a purchase's cost corrected after its till closed says
 * the till paid out more than it was told.
 */
const CONTEOS: ReadonlySet<string> = new Set([
  'caja.abierta',
  'caja.cerrada',
  'caja.entregada',
  'efectivo.recibido',
  'jornada.liquidada',
  'efectivo.arqueo',
]);

export async function reduceGasto(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'gasto.registrado':
      return registrar(event, read, rev);
    case 'faltante.explicado':
      return explicarFaltante(event, read, rev);
    case 'gasto.anulado':
      return anular(event, read, rev);
    case 'compra.pagada':
      return pagarCompra(event, read, rev);
    case 'gasto.pagado':
      return pagarGasto(event, read, rev);
    case 'compra.pago_anulado':
    case 'gasto.pago_anulado':
      return anularPago(event, read, rev);
    case 'dueno.retiro':
    case 'dueno.aporte':
      return movimientoDueno(event, read, rev);
    case 'dueno.movimiento_anulado':
      return anularMovimientoDueno(event, read, rev);
    default:
      return null;
  }
}

async function registrar(
  event: Extract<AnyDomainEvent, { type: 'gasto.registrado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.gasto(p.gastoId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese gasto ya fue registrado' }] };
  }
  if (p.proveedorId && !(await read.proveedor(p.proveedorId))) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'El proveedor no existe' }] };
  }

  /** Paid in parts (`pagos`): each its own way, and `formaPago` what they leave. */
  const partes = p.pagos ?? [];
  const config = await read.config();
  for (const cuenta of [p.cuenta, ...partes.map((x) => x.cuenta)]) {
    const cerrada = cuentaCerradaParaSiempre(config, cuenta);
    if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  }

  const monto = centavos(p.monto);
  /*
   * Dollars paid out of general cash leave it at the official rate, which is what they are carried
   * at there, and so do those paid from a dollar account (`bancos.ts`); otherwise the rate the
   * expense came with. A part in dollars takes the whole expense with it, as on a purchase: one
   * rate to an expense.
   */
  const deCuentaEnDolares = p.formaPago === 'transferencia' && p.cuenta !== undefined && esCuentaEnDolares(p.cuenta);
  const deDolares = p.formaPago === 'efectivo_dolares' || deCuentaEnDolares || partes.some((x) => x.formaPago === 'efectivo_dolares');
  const tasa = p.moneda !== 'USD' ? null : fxRate(deDolares ? config.fxTasaOficial : p.tasaCambio!);
  const montoCordobas = tasa === null ? monto : usdToCordobas(monto, tasa);

  /**
   * The parts in córdobas, each converted on its own, and what they leave for `formaPago`: the
   * whole less the parts, so whatever the conversions rounded lands there and the entry balances.
   */
  const partesCordobas = partes.map((x) => (tasa === null ? centavos(x.monto) : usdToCordobas(centavos(x.monto), tasa)));
  const resto = partesCordobas.reduce((a, x) => subMoney(a, x), montoCordobas);
  if (partes.length > 0 && resto <= 0) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Las partes pagan todo el gasto: la última va en la forma de pago' }] };
  }
  /** The rest in the expense's own currency: what leaves a dollar account that pays it. */
  const restoMoneda = centavos(p.monto - partes.reduce((a, x) => a + x.monto, 0));

  /**
   * What leaves an open till: the rest when it is paid from there, and every part that is. One till
   * and one patch of it, since the reader cannot see a patch before the next one.
   */
  const gaveta = p.sesionId ?? partes.find((x) => x.sesionId)?.sesionId;
  if (partes.some((x) => x.sesionId && x.sesionId !== gaveta)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Lo que sale de la gaveta sale de una sola caja' }] };
  }
  const deLaGaveta = partes.reduce((a, x, i) => (x.sesionId ? addMoney(a, partesCordobas[i]!) : a), p.sesionId ? resto : ZERO);
  const deCaja = gaveta ? await pagoDeCaja(read, gaveta, p.sesionId ? p.formaPago : 'efectivo_cordobas', deLaGaveta, rev) : null;
  if (deCaja && 'rechazo' in deCaja) {
    return { effects: [], rejections: [{ eventId: event.id, reason: deCaja.rechazo }] };
  }

  const gasto: Gasto = {
    id: p.gastoId,
    fecha: p.fecha,
    categoria: p.categoria,
    descripcion: p.descripcion,
    moneda: p.moneda,
    monto,
    tasaCambio: tasa,
    montoCordobas,
    formaPago: p.formaPago,
    // The till the rest came out of; each part keeps its own.
    sesionId: deCaja && p.sesionId ? p.sesionId : null,
    ...(p.cuenta ? { cuenta: p.cuenta } : {}),
    proveedorId: p.proveedorId,
    referencia: p.referencia ?? null,
    nota: p.nota ?? null,
    foto: p.foto ?? null,
    ...(partes.length > 0
      ? {
          // On credit, what the parts paid is paid: what is owed is what they left (`pendienteDeGasto`).
          ...(p.formaPago === 'credito' ? { pagado: subMoney(montoCordobas, resto) } : {}),
          pagos: partes.map((x, i) => ({
            id: x.pagoId,
            fecha: p.fecha,
            monto: partesCordobas[i]!,
            ...(p.moneda === 'USD' ? { dolares: centavos(x.monto) } : {}),
            formaPago: x.formaPago,
            sesionId: x.sesionId ?? null,
            ...(x.cuenta ? { cuenta: x.cuenta } : {}),
            referencia: null,
            anulado: false,
            motivoAnulacion: null,
            lugar: lugarEnElLog(event),
            rev,
            conElGasto: true as const,
          })),
        }
      : {}),
    anulado: false,
    motivoAnulacion: null,
    lugar: lugarEnElLog(event),
    rev,
  };

  /** A credit line where the money came from: the drawer, or the account its way of paying names. */
  const pagadoCon = (forma: FormaPago, deLaGaveta: boolean, cordobas: Centavos, cuenta: string | undefined, dolares = 0) =>
    deLaGaveta
      ? credit('CAJA_CORDOBAS', cordobas, 'Pagado de la gaveta')
      : lineaDelPago('haber', cuentaDelPago(forma, cuenta), cordobas, dolares);
  return {
    effects: [
      { kind: 'gasto.upsert', id: p.gastoId, row: gasto },
      ...(deCaja ? [deCaja.efecto] : []),
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `${ETIQUETA_CATEGORIA[p.categoria]}: ${p.descripcion}`, [
          debit(CUENTA_CATEGORIA[p.categoria], montoCordobas),
          ...partes.map((x, i) => pagadoCon(x.formaPago, Boolean(x.sesionId), partesCordobas[i]!, x.cuenta)),
          pagadoCon(p.formaPago, Boolean(deCaja && p.sesionId), resto, p.cuenta, p.moneda === 'USD' ? restoMoneda : 0),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * A shortage explained (`faltante.explicado`): what a count found missing went on this. An
 * expense that moves no money, since the count already took it out of the cash, and comes off
 * the shortage instead, on the count's day: never past what that count found missing, less what
 * is explained of it already.
 */
async function explicarFaltante(
  event: Extract<AnyDomainEvent, { type: 'faltante.explicado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  if (await read.gasto(p.gastoId)) return rechazo('Ese gasto ya fue registrado');
  const delConteo = await read.eventoDelLog(p.eventoId);
  if (!delConteo || !CONTEOS.has(delConteo.tipo)) return rechazo('Eso no fue un conteo: solo se explica lo que faltó al contar');
  const tipo = delConteo.tipo;

  // What the count booked missing: a till's shortage, or general cash's.
  const asientos = await read.asientosDeEvento(p.eventoId);
  const falto = (cuenta: 'FALTANTE_CAJA' | 'FALTANTE_GENERAL') =>
    asientos.flatMap((a) => a.lineas).reduce((s, l) => s + (l.cuenta === ACCOUNTS[cuenta].code ? l.debe - l.haber : 0), 0);
  // A count a correction reached found what the books now keep of it (`encontrado.ts`), on the
  // shortage account of its kind: general cash's, or a till's.
  const corregido = (await read.config()).conteosCorregidos[p.eventoId];
  const faltaba = corregido !== undefined ? Math.max(-corregido, 0) : null;
  const cuenta =
    faltaba !== null
      ? faltaba > 0
        ? tipo === 'efectivo.arqueo'
          ? 'FALTANTE_GENERAL'
          : 'FALTANTE_CAJA'
        : null
      : falto('FALTANTE_CAJA') > 0
        ? 'FALTANTE_CAJA'
        : falto('FALTANTE_GENERAL') > 0
          ? 'FALTANTE_GENERAL'
          : null;
  if (!cuenta) return rechazo('Ese conteo no encontró nada faltante');
  const explicado = (await read.gastosDeFaltante(p.eventoId)).reduce((s, g) => s + (g.anulado ? 0 : g.montoCordobas), 0);
  const queda = (faltaba ?? falto(cuenta)) - explicado;
  const monto = centavos(p.monto);
  if (monto > queda) {
    return rechazo(queda > 0 ? `Es más de lo que faltó: quedan ${formatMoney(centavos(queda))} por explicar` : 'Lo que faltó en ese conteo ya está explicado');
  }

  // A count that booked nothing and was corrected to a shortage is on its own day all the same:
  // general cash's too, which went on the explanation's day (2026-10-20).
  const fecha = asientos[0]?.fecha ?? fechaEnNicaragua(delConteo.clientTs);
  const gasto: Gasto = {
    id: p.gastoId,
    fecha,
    categoria: p.categoria,
    descripcion: p.descripcion,
    moneda: 'NIO',
    monto,
    tasaCambio: null,
    montoCordobas: monto,
    // It was cash; the count already took it out of where it was.
    formaPago: 'efectivo_cordobas',
    sesionId: null,
    proveedorId: null,
    referencia: null,
    nota: p.nota ?? null,
    foto: null,
    faltanteDe: p.eventoId,
    faltanteCuenta: cuenta,
    anulado: false,
    motivoAnulacion: null,
    lugar: lugarEnElLog(event),
    rev,
  };
  return {
    effects: [
      { kind: 'gasto.upsert', id: p.gastoId, row: gasto },
      {
        kind: 'asiento',
        entry: entry(event.id, fecha, `${ETIQUETA_CATEGORIA[p.categoria]}: ${p.descripcion}`, [
          debit(CUENTA_CATEGORIA[p.categoria], monto),
          credit(cuenta, monto, 'Lo que faltó al contar'),
        ]),
      },
    ],
    rejections: [],
  };
}

async function anular(
  event: Extract<AnyDomainEvent, { type: 'gasto.anulado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const gasto = await read.gasto(p.gastoId);

  if (!gasto) return rechazo('Ese gasto no existe');
  if (gasto.anulado) return rechazo('Ese gasto ya fue anulado');
  // Its own parts go back with it (`conElGasto`); a payment made after it is another fact.
  const partes = (gasto.pagos ?? []).filter((x) => x.conElGasto && !x.anulado);
  const enPartes = partes.reduce((a, x) => addMoney(a, x.monto), ZERO);
  if (gasto.formaPago === 'credito' && (gasto.pagado ?? 0) > enPartes) {
    // Reversing the whole debt would erase payments that really happened, as with a purchase.
    return rechazo('Ese gasto ya tiene pagos: no se puede anular');
  }
  if (p.reemplazo !== undefined) {
    // What it explains was never paid out: a record in its place would pay it out after all.
    if (gasto.faltanteDe) return rechazo('Lo que explica un faltante no se corrige: se anula y se explica de nuevo');
    if (p.reemplazo === p.gastoId) return rechazo('La corrección de ese gasto no quedó registrada: el gasto sigue como estaba');
    // A correction: the new record goes first, and only if it stands does the old one go — unless
    // the old amount has to be back in the drawer before the new one can be paid (`reemplazoDespues`).
    if (!p.reemplazoDespues) {
      const nuevo = await read.gasto(p.reemplazo);
      if (!nuevo || nuevo.anulado) return rechazo('La corrección de ese gasto no quedó registrada: el gasto sigue como estaba');
    }
  }
  if ((p.reemplazo === undefined || p.reemplazoDespues) && p.sinContar && !gasto.faltanteDe) {
    /*
     * Voided, the money goes back where it was paid from, and a count since already found it
     * there: the money that never went was in the count, booked as a surplus. Out of a till only
     * while it is open and nobody has received it since; otherwise until general cash, the bank
     * or the wallet is counted again (`contadas.ts`). A shortage explained moved no money, nor did
     * what is owed on credit: its parts did, each from where it came.
     */
    const lugar = gasto.lugar ?? gasto.rev;
    for (const sesionId of new Set([gasto.sesionId, ...partes.map((x) => x.sesionId)].filter((x): x is string => Boolean(x)))) {
      const sesion = await read.sesionCaja(sesionId);
      if (sesion?.estado !== 'abierta') return rechazo('Ese gasto salió de una caja que ya se cerró: su corte ya lo contó');
      if ((sesion.entregadaRev ?? -Infinity) > lugar) {
        return rechazo('Después de ese gasto la caja cambió de manos: esa entrega ya contó la gaveta y anotó la diferencia');
      }
    }
    const deCuentas = [
      ...(!gasto.sesionId && gasto.formaPago !== 'credito' ? [cuentaDelPago(gasto.formaPago, gasto.cuenta)] : []),
      ...partes.filter((x) => !x.sesionId).map((x) => cuentaDelPago(x.formaPago, x.cuenta)),
    ];
    if (deCuentas.length > 0) {
      const config = await read.config();
      for (const cuenta of deCuentas) {
        if (contadaDespues(config, cuenta, lugar, gasto.fecha)) return rechazo(yaLaContaron(cuenta, 'ese gasto', config));
      }
    }
  }

  /**
   * Paid out of a drawer, it goes back into it while that till is open: the rest when it came from
   * there, and the parts that did, in one patch of the till.
   */
  const resto = subMoney(gasto.montoCordobas, enPartes);
  const gaveta = gasto.sesionId ?? partes.find((x) => x.sesionId)?.sesionId;
  const aLaGaveta = partes.reduce((a, x) => (x.sesionId ? addMoney(a, x.monto) : a), gasto.sesionId ? resto : ZERO);
  const vuelta = gaveta ? await devolucionDePago(read, gaveta, aLaGaveta, rev) : null;
  /** Where money of it goes back to: the drawer it left, while that till is open, or its own account. */
  const vuelveA = (forma: FormaPago, deLaGaveta: boolean, cuenta: string | null | undefined): AccountKey =>
    deLaGaveta && vuelta ? vuelta.cuenta : cuentaDelPago(forma, cuenta);
  // A shortage explained moved no money: voided, the shortage is back as the count found it.
  const deVuelta = gasto.faltanteCuenta ?? vuelveA(gasto.formaPago, Boolean(gasto.sesionId), gasto.cuenta);
  /** The rest in the expense's own currency: what a dollar account that paid it gets back. */
  const restoMoneda = gasto.monto - partes.reduce((a, x) => a + (x.dolares ?? 0), 0);

  // Reversing entry, not a deletion. A wrong expense stays visible alongside its correction.
  return {
    effects: [
      {
        kind: 'gasto.patch',
        id: p.gastoId,
        rev,
        patch: {
          anulado: true,
          motivoAnulacion: p.motivo,
          // Its parts went back with it: none is there to void again.
          ...(partes.length > 0
            ? {
                pagos: gasto.pagos!.map((x) =>
                  x.conElGasto && !x.anulado ? { ...x, anulado: true, motivoAnulacion: p.motivo, anuladoConElGasto: true as const } : x,
                ),
              }
            : {}),
        },
      },
      ...(vuelta?.efectos ?? []),
      {
        kind: 'asiento',
        entry: entry(event.id, gasto.fecha, `Anulación de gasto: ${p.motivo}`, [
          ...partes.map((x) => lineaDelPago('debe', vuelveA(x.formaPago, Boolean(x.sesionId), x.cuenta), x.monto, 0)),
          lineaDelPago('debe', deVuelta, resto, gasto.moneda === 'USD' ? restoMoneda : 0),
          credit(CUENTA_CATEGORIA[gasto.categoria], gasto.montoCordobas),
        ]),
      },
    ],
    rejections: [],
  };
}

async function pagarCompra(
  event: Extract<AnyDomainEvent, { type: 'compra.pagada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const compra = await read.compra(p.compraId);

  if (!compra) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra no existe' }] };
  }
  if (compra.anulada) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa compra fue anulada' }] };
  }
  if (compra.formaPago !== 'credito') {
    return {
      effects: [],
      rejections: [{ eventId: event.id, reason: 'Esa compra no quedó a crédito: ya estaba pagada' }],
    };
  }

  const monto = centavos(p.monto);
  // Goods sent back against this purchase are already off what is owed.
  const pendiente = pendienteDeCompra(compra);
  if (monto > pendiente) {
    return {
      effects: [],
      rejections: [
        {
          eventId: event.id,
          // Paid off meanwhile, from another phone say: «faltan C$ 0.00» said it, less plainly.
          reason: pendiente > 0 ? `Está pagando más de lo que se debe: faltan ${formatMoney(pendiente)}` : 'De esa compra ya no se debe nada',
        },
      ],
    };
  }

  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  const pagado = addMoney(compra.pagado, monto);
  const deCaja = p.sesionId ? await pagoDeCaja(read, p.sesionId, p.formaPago, monto, rev) : null;
  if (deCaja && 'rechazo' in deCaja) {
    return { effects: [], rejections: [{ eventId: event.id, reason: deCaja.rechazo }] };
  }
  const pagos = conPago(compra.pagos, p, monto, deCaja ? p.sesionId! : null, lugarEnElLog(event), rev);
  if (pagos === 'repetido') return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese pago ya está registrado' }] };

  return {
    effects: [
      { kind: 'compra.patch', id: p.compraId, rev, patch: { pagado, ...(pagos ? { pagos } : {}) } },
      ...(deCaja ? [deCaja.efecto] : []),
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `Pago de compra${p.referencia ? ` (${p.referencia})` : ''}`, [
          debit('CUENTAS_POR_PAGAR', monto),
          credit(deCaja ? 'CAJA_CORDOBAS' : cuentaDelPago(p.formaPago, p.cuenta), monto, deCaja ? 'Pagado de la gaveta' : undefined),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * Paying an expense bought on credit: the bill that came before the money did. Off what is owed,
 * as a purchase is paid (`pagarCompra`), and never past it.
 */
async function pagarGasto(
  event: Extract<AnyDomainEvent, { type: 'gasto.pagado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });

  const gasto = await read.gasto(p.gastoId);
  if (!gasto) return rechazo('Ese gasto no existe');
  if (gasto.anulado) return rechazo('Ese gasto fue anulado');
  if (gasto.formaPago !== 'credito') return rechazo('Ese gasto no quedó a crédito: ya estaba pagado');

  const monto = centavos(p.monto);
  const pendiente = pendienteDeGasto(gasto);
  if (monto > pendiente) {
    return rechazo(pendiente > 0 ? `Está pagando más de lo que se debe: faltan ${formatMoney(pendiente)}` : 'Ese gasto ya está pagado');
  }

  const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
  if (cerrada) return rechazo(cerrada);
  const deCaja = p.sesionId ? await pagoDeCaja(read, p.sesionId, p.formaPago, monto, rev) : null;
  if (deCaja && 'rechazo' in deCaja) return rechazo(deCaja.rechazo);
  const pagos = conPago(gasto.pagos, p, monto, deCaja ? p.sesionId! : null, lugarEnElLog(event), rev);
  if (pagos === 'repetido') return rechazo('Ese pago ya está registrado');

  return {
    effects: [
      { kind: 'gasto.patch', id: gasto.id, rev, patch: { pagado: addMoney(gasto.pagado ?? ZERO, monto), ...(pagos ? { pagos } : {}) } },
      ...(deCaja ? [deCaja.efecto] : []),
      {
        kind: 'asiento',
        entry: entry(event.id, p.fecha, `Pago de ${gasto.descripcion}${p.referencia ? ` (${p.referencia})` : ''}`, [
          debit('CUENTAS_POR_PAGAR', monto),
          credit(deCaja ? 'CAJA_CORDOBAS' : cuentaDelPago(p.formaPago, p.cuenta), monto, deCaja ? 'Pagado de la gaveta' : undefined),
        ]),
      },
    ],
    rejections: [],
  };
}

/**
 * The debt's payments with this one, when it names itself (`pagoId`) so it can be voided: null
 * when it does not, as every payment from before did.
 */
function conPago(
  pagos: readonly PagoProveedor[] | undefined,
  p: {
    pagoId?: string | undefined;
    fecha: string;
    formaPago: PagoProveedor['formaPago'];
    cuenta?: CuentaBanco | undefined;
    referencia?: string | undefined;
  },
  monto: PagoProveedor['monto'],
  sesionId: string | null,
  lugar: number,
  rev: number,
): PagoProveedor[] | 'repetido' | null {
  if (!p.pagoId) return null;
  if ((pagos ?? []).some((x) => x.id === p.pagoId)) return 'repetido';
  return [
    ...(pagos ?? []),
    {
      id: p.pagoId,
      fecha: p.fecha,
      monto,
      formaPago: p.formaPago,
      sesionId,
      ...(p.cuenta ? { cuenta: p.cuenta } : {}),
      referencia: p.referencia ?? null,
      anulado: false,
      motivoAnulacion: null,
      lugar,
      rev,
    },
  ];
}

/**
 * A supplier's payment that was not: back where it came from, and owed again. Out of a till only
 * while that till is open, whose corte has not counted it yet; out of general cash, the bank or
 * the wallet only until that money is counted again (`contadas.ts`). Either count found it.
 */
async function anularPago(
  event: Extract<AnyDomainEvent, { type: 'compra.pago_anulado' | 'gasto.pago_anulado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const deuda =
    event.type === 'compra.pago_anulado' ? await read.compra(event.payload.compraId) : await read.gasto(event.payload.gastoId);
  if (!deuda) return rechazo(event.type === 'compra.pago_anulado' ? 'Esa compra no existe' : 'Ese gasto no existe');
  const { pagoId, motivo } = event.payload;
  const pago = (deuda.pagos ?? []).find((x) => x.id === pagoId);
  if (!pago) return rechazo('Ese pago no se puede anular: es de antes de que los pagos se pudieran anular');
  if (pago.anulado) return rechazo('Ese pago ya fue anulado');
  // Voided, nothing is owed on it to put a payment back on. Its own parts were voided with it, so
  // this only holds the line should a voided debt ever keep a payment standing.
  if ('anulada' in deuda ? deuda.anulada : deuda.anulado) {
    return rechazo(event.type === 'compra.pago_anulado' ? 'Esa compra fue anulada' : 'Ese gasto fue anulado');
  }
  // A part of a purchase paid in full when it came: nothing is owed to put it back on.
  if (pago.conLaCompra && deuda.formaPago !== 'credito') {
    return rechazo('Ese pago se hizo con la compra, que quedó pagada entera: se deshace anulando la compra');
  }
  // Nor of an expense paid in full when it was recorded.
  if (pago.conElGasto && deuda.formaPago !== 'credito') {
    return rechazo('Ese pago se hizo con el gasto, que quedó pagado entero: se deshace anulando el gasto');
  }

  let cuenta: AccountKey = cuentaDelPago(pago.formaPago, pago.cuenta);
  const config = await read.config();
  const efectos: Effect[] = [];
  if (pago.sesionId) {
    // The drawer is counted at its corte and at every handover: after either, that count found it.
    const sesion = await read.sesionCaja(pago.sesionId);
    if (sesion?.estado !== 'abierta') return rechazo('Ese pago salió de una caja que ya se cerró: su corte ya lo contó');
    if ((sesion.entregadaRev ?? -Infinity) > (pago.lugar ?? pago.rev)) {
      return rechazo('Después de ese pago la caja cambió de manos: esa entrega ya contó la gaveta y anotó la diferencia');
    }
    const vuelta = await devolucionDePago(read, pago.sesionId, pago.monto, rev);
    cuenta = vuelta.cuenta;
    efectos.push(...vuelta.efectos);
  } else if (contadaDespues(config, cuenta, pago.lugar ?? pago.rev, pago.fecha)) {
    return rechazo(yaLaContaron(cuenta, 'ese pago', config));
  }

  const pagos = (deuda.pagos ?? []).map((x) => (x.id === pagoId ? { ...x, anulado: true, motivoAnulacion: motivo } : x));
  const pagado = subMoney(deuda.pagado ?? ZERO, pago.monto);
  return {
    effects: [
      event.type === 'compra.pago_anulado'
        ? { kind: 'compra.patch', id: deuda.id, rev, patch: { pagado, pagos } }
        : { kind: 'gasto.patch', id: deuda.id, rev, patch: { pagado, pagos } },
      ...efectos,
      {
        kind: 'asiento',
        // On the payment's own day: the money never left that day.
        entry: entry(event.id, pago.fecha, `Anulación de pago${pago.referencia ? ` (${pago.referencia})` : ''}: ${motivo}`, [
          debit(cuenta, pago.monto, cuenta === 'CAJA_CORDOBAS' ? 'Vuelve a la gaveta' : undefined),
          credit('CUENTAS_POR_PAGAR', pago.monto),
        ]),
      },
    ],
    rejections: [],
  };
}

async function movimientoDueno(
  event: Extract<AnyDomainEvent, { type: 'dueno.retiro' | 'dueno.aporte' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;

  if (await read.movimientoDueno(p.movimientoId)) {
    return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese movimiento ya fue registrado' }] };
  }

  const forma: FormaPago =
    event.type === 'dueno.retiro' ? event.payload.origen : event.payload.destino;
  const esRetiro = event.type === 'dueno.retiro';
  const config = await read.config();
  const cerrada = cuentaCerradaParaSiempre(config, p.cuenta);
  if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
  const cuenta = cuentaDelPago(forma, p.cuenta);
  /*
   * Into or out of a dollar account it is said in dollars (`bancos.ts`), and goes on at the official
   * rate, as a deposit's dollars do: the books' own figure, whatever the phone showed beside it.
   */
  const dolares = p.dolares !== undefined ? centavos(p.dolares) : null;
  const monto = dolares !== null ? alOficial(dolares, fxRate(config.fxTasaOficial)) : centavos(p.monto);

  /**
   * Neither of these touches income or expense.
   *
   * A withdrawal reduces equity and an injection increases it. Booking a withdrawal as a cost
   * is the most common way a family business's profit figure ends up wrong, and it is wrong in
   * the direction that hides how much the business is actually making.
   */
  const lineas = esRetiro
    ? [debit('RETIROS_DUENO', monto, p.motivo), lineaDelPago('haber', cuenta, monto, dolares ?? 0)]
    : [lineaDelPago('debe', cuenta, monto, dolares ?? 0), credit('CAPITAL', monto, p.motivo)];

  const row: MovimientoDueno = {
    id: p.movimientoId,
    fecha: p.fecha,
    tipo: esRetiro ? 'retiro' : 'aporte',
    monto,
    cuenta,
    ...(dolares !== null ? { dolares } : {}),
    motivo: p.motivo ?? null,
    lugar: lugarEnElLog(event),
    rev,
  };

  return {
    effects: [
      { kind: 'movimientoDueno.upsert', id: p.movimientoId, row },
      {
        kind: 'asiento',
        entry: entry(
          event.id,
          p.fecha,
          esRetiro ? 'Retiro del dueño' : 'Aporte del dueño',
          lineas,
        ),
      },
    ],
    rejections: [],
  };
}

/**
 * Money for or from the owner that was not: back where it came from. Only until that money is
 * counted again (`contadas.ts`): a count since then already found it.
 */
async function anularMovimientoDueno(
  event: Extract<AnyDomainEvent, { type: 'dueno.movimiento_anulado' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const mov = await read.movimientoDueno(p.movimientoId);
  if (!mov) return rechazo('Ese movimiento no existe');
  if (mov.anulado) return rechazo('Ese movimiento ya fue anulado');
  const cuenta = mov.cuenta as AccountKey;
  const retiro = mov.tipo === 'retiro';
  const config = await read.config();
  if (contadaDespues(config, cuenta, mov.lugar ?? mov.rev, mov.fecha)) {
    return rechazo(yaLaContaron(cuenta, retiro ? 'ese retiro' : 'ese aporte', config));
  }

  return {
    effects: [
      { kind: 'movimientoDueno.patch', id: mov.id, rev, patch: { anulado: true, motivoAnulacion: p.motivo } },
      {
        kind: 'asiento',
        entry: entry(
          event.id,
          mov.fecha,
          `Anulación de ${retiro ? 'retiro' : 'aporte'} del dueño: ${p.motivo}`,
          retiro
            ? [lineaDelPago('debe', cuenta, mov.monto, mov.dolares ?? 0, 'Vuelve'), credit('RETIROS_DUENO', mov.monto)]
            : [debit('CAPITAL', mov.monto), lineaDelPago('haber', cuenta, mov.monto, mov.dolares ?? 0, 'Vuelve al dueño')],
        ),
      },
    ],
    rejections: [],
  };
}
